import { useState, useEffect } from 'react';
import { useSubscriptionStore } from '@/features/auth/stores/subscription.store';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Formats a price amount from cents to a currency string
 */
const formatPrice = (amount: number, currency: string = 'usd'): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  });

  return formatter.format(amount / 100);
};

/**
 * Modal component for displaying subscription details and initiating checkout
 */
export const SubscriptionModal = ({ isOpen, onClose }: SubscriptionModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { createSubscription, standardApplicationPrice, checkSubscription } =
    useSubscriptionStore();

  useEffect(() => {
    if (isOpen) {
      checkSubscription();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubscribe = async () => {
    try {
      setIsProcessing(true);
      const checkoutUrl = await createSubscription();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to create subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Get the formatted application price or fallback to default
  const getApplicationPriceDisplay = () => {
    if (standardApplicationPrice?.unit_amount) {
      return formatPrice(standardApplicationPrice.unit_amount, standardApplicationPrice.currency);
    }
    return '$2.50'; // Fallback price if not available from API
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="subscription-modal"
    >
      <div className="bg-white rounded-lg p-8 max-w-md w-full m-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Plan Details */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-4">Standard</h2>
          <p className="text-lg text-gray-600 mb-6">
            Process applications with flexible pricing based on your needs.
          </p>

          <div className="space-y-4">
            {/* Base Price */}
            <div className="flex justify-between items-center text-lg">
              <span>Base Price</span>
              <span className="font-medium">$125/month</span>
            </div>

            {/* Application Rates */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h4 className="font-medium text-gray-900">Application Processing</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Applications</span>
                    <p className="text-xs">Complete application processing</p>
                  </div>
                  <span>{getApplicationPriceDisplay()} each</span>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSubscribe}
            disabled={isProcessing}
            className="w-full bg-blue-600 text-white rounded-full py-4 mt-8 text-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Subscribe Now'}
          </button>

          <p className="text-sm text-gray-500 mt-4 text-center">
            Pay only for the applications you process.
          </p>
        </div>
      </div>
    </div>
  );
};
